import React from 'react';

import { formatAvailabilityForTiles } from '@kyruus/availability-tiles';
import { FormattedMessage } from '@kyruus/intl';
import {
  ProviderAvailability,
  ProviderBookingButton,
  Slot
} from '@kyruus/provider-components';

import {
  AvailabilityModeBookingContent,
  BookingAsideContainer,
  BookingButtonModeBookingContent,
  BookingButtonWrapper,
  BookingHeader,
  StyledScheduleAppointmentAside
} from './styles';
import messages from '../../lib/messages';
import { LoadingBookingOptions } from './LoadingBookingOptions';
import ProfileContact from '../ProfileContact/profile-contact';
import { ScheduleAppointmentProps } from './types';
import { BookingPanelStickyBox } from '../ProviderProfile/styles';
import {
  getBookingStrategy,
  showAvailableWithinDays,
  getOpenSchedulingConfig,
  getShouldRenderDirectBookInDrawer,
  getShowRegulatedSpacesBanner,
  getMaxAvailabilitiesToDisplay
} from './utilities';
import AvailableWithinDays from './AvailableWithinDays';
import { Case, Default, Switch } from '../Switch';
import { BookingStrategies } from '../../types';
import OpenSchedulingScheduleAppointment from '../OpenScheduling/OpenSchedulingScheduleAppointment';
import EstimatedWaitTimeSection from '../EstimatedWaitTime/EstimatedWaitTime';

export type ScheduleAppointmentAsideProps = ScheduleAppointmentProps & {
  showScheduleAppointmentAside: boolean;
};

export function ScheduleAppointmentAside(props: ScheduleAppointmentAsideProps) {
  const bookingStrategy = getBookingStrategy(
    props.config,
    props.provider,
    props.availability
  );

  const isLoading = props.loadingBookingInfo;

  const shouldRenderDirectBookInDrawer = getShouldRenderDirectBookInDrawer(
    props.config
  );
  const showRegulatedSpacesBanner = getShowRegulatedSpacesBanner(props.config);
  const maxAvailabilitiesToDisplay = getMaxAvailabilitiesToDisplay(
    props.config
  );

  return (
    <StyledScheduleAppointmentAside id="schedule-appointment-aside">
      <BookingPanelStickyBox config={props.config}>
        {props.showWaitTimeSection && (
          <EstimatedWaitTimeSection
            locations={props.locations}
            mlocations={props.mlocations}
            provider={props.provider}
            config={props.config}
            track={props.track}
            showWaitTimeButton={props.showWaitTimeButton}
            getWaitTimeCallbackFunction={props.getWaitTimeCallbackFunction}
            waitTimeMap={props.waitTimeMap}
          />
        )}
        {props.showScheduleAppointmentAside && (
          <BookingAsideContainer aria-labelledby="ScheduleAppointmentAsideHeader">
            <BookingHeader
              id="ScheduleAppointmentAsideHeader"
              data-testid="booking-header"
            >
              <FormattedMessage {...messages.bookingHeader} />
            </BookingHeader>

            <LoadingBookingOptions loading={isLoading} />

            {!isLoading && (
              <Switch on={bookingStrategy}>
                <Case when={BookingStrategies.Availability}>
                  <AvailabilityModeBookingContent>
                    <ProviderAvailability
                      provider={props.provider}
                      config={props.config}
                      appointmentInfo={props.availability.apptInfo || {}}
                      locations={props.locations || []}
                      loggingFunction={props.track}
                      maxAvailabilitiesToDisplay={maxAvailabilitiesToDisplay}
                      onTimeSlotsClick={props.availability.handleTimeSlotsClick}
                      onViewMoreClick={props.availability.handleViewMoreClick}
                      purposeOptionsByRelationship={
                        props.purposeOptionsByRelationship
                      }
                      services={{
                        formatAvailability: (slots: Slot[]) => {
                          return formatAvailabilityForTiles(
                            slots,
                            props.availTilesBookOnlineUrl,
                            false
                          );
                        },
                        fetchAvailability: props.fetchProviderAvailability
                      }}
                      shouldRenderSlotsAsButtons={
                        shouldRenderDirectBookInDrawer
                      }
                      showRegulatedSpacesMessage={showRegulatedSpacesBanner}
                      bookSrc={props.bookSrc}
                      bookOnlineUrl={props.availTilesBookOnlineUrl}
                    />
                  </AvailabilityModeBookingContent>
                </Case>
                <Case when={BookingStrategies.OpenSchedulingWidget}>
                  <OpenSchedulingScheduleAppointment
                    config={getOpenSchedulingConfig(props.config)}
                    provider={props.provider}
                    track={props.track}
                  />
                </Case>
                <Case when={BookingStrategies.Button}>
                  <BookingButtonModeBookingContent>
                    <BookingButtonWrapper>
                      <ProviderBookingButton
                        provider={props.provider}
                        modalDisplayConfig={props.config.modal_display || {}}
                        onClick={props.onBook}
                        href={props.bookOnlineUrl}
                      />
                    </BookingButtonWrapper>
                    {showAvailableWithinDays(props) && (
                      <AvailableWithinDays
                        availabilityDensityBest={props.availabilityDensityBest}
                      />
                    )}
                  </BookingButtonModeBookingContent>
                </Case>
                <Default>
                  <>Not implemented.</>
                </Default>
              </Switch>
            )}
            <ProfileContact provider={props.provider} track={props.track} />
          </BookingAsideContainer>
        )}
      </BookingPanelStickyBox>
    </StyledScheduleAppointmentAside>
  );
}
